'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, AlertCircle, FileSearch, Loader2, Wifi, Shield, Activity, Database, Lock, Cpu, Radio, X, Bell, Calendar, DollarSign } from 'lucide-react';
import { findCaseByDocument, getCaseById, type Case } from '@/lib/supabaseData';
import { createClient } from '@/lib/supabase/client';
import CaseResultCard from './CaseResultCard';
import Icon from '@/components/ui/AppIcon';


type SearchState = 'idle' | 'tracking' | 'loading' | 'found' | 'not-found';

// ─── Tracking Animation Screen ────────────────────────────────────────────────
function TrackingScreen({ docNumber }: { docNumber: string }) {
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [scanLine, setScanLine] = useState(0);

  const steps = [
    { icon: Shield, label: 'Verificando identidad en sistema nacional…', color: '#c9a84c' },
    { icon: Database, label: 'Accediendo a registros financieros…', color: '#c9a84c' },
    { icon: Activity, label: 'Rastreando movimientos de capitales…', color: '#c9a84c' },
    { icon: Cpu, label: 'Analizando estado del proceso judicial…', color: '#c9a84c' },
    { icon: Lock, label: 'Consolidando información confidencial…', color: '#c9a84c' },
    { icon: Radio, label: 'Preparando reporte de caso…', color: '#22c55e' },
  ];

  useEffect(() => {
    // Progress bar — completes in ~10 seconds (100 / 0.36 * 36ms ≈ 10 000ms)
    const progressInterval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) { clearInterval(progressInterval); return 100; }
        return p + 0.36;
      });
    }, 36);

    // Step advancement — 6 steps over ~9.6 seconds (1600ms each)
    const stepInterval = setInterval(() => {
      setStep((s) => (s < steps.length - 1 ? s + 1 : s));
    }, 1600);

    // Scan line animation
    const scanInterval = setInterval(() => {
      setScanLine((l) => (l >= 100 ? 0 : l + 2));
    }, 30);

    return () => {
      clearInterval(progressInterval);
      clearInterval(stepInterval);
      clearInterval(scanInterval);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d0d1a]/95 backdrop-blur-sm">
      {/* Grid background */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            'linear-gradient(#c9a84c22 1px, transparent 1px), linear-gradient(90deg, #c9a84c22 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Scan line */}
      <div
        className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent opacity-40 pointer-events-none"
        style={{ top: `${scanLine}%`, transition: 'top 0.03s linear' }}
      />

      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#c9a84c]/10 border border-[#c9a84c]/40 px-4 py-1.5 mb-5">
            <span className="w-2 h-2 rounded-full bg-[#c9a84c] animate-pulse" />
            <span className="text-[#c9a84c] text-xs font-bold tracking-widest uppercase">
              Sistema Activo
            </span>
          </div>
          <h2 className="text-white text-2xl font-bold mb-1 tracking-tight">
            RASTREO DE CAPITALES
          </h2>
          <p className="text-[#a89070] text-xs tracking-widest uppercase">
            Consulta de Estado de Proceso
          </p>
        </div>

        {/* Document display */}
        <div className="bg-[#1a1a2e] border border-[#c9a84c]/30 p-4 mb-6 text-center">
          <p className="text-[#a89070] text-xs uppercase tracking-widest mb-1">
            Documento en Verificación
          </p>
          <p className="text-[#c9a84c] font-mono text-xl font-bold tracking-widest">
            {docNumber.replace(/(.{3})/g, '$1 ').trim()}
          </p>
        </div>

        {/* Steps list */}
        <div className="space-y-2 mb-6">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isDone = i < step;
            const isActive = i === step;
            return (
              <div
                key={i}
                className={`flex items-center gap-3 px-4 py-2.5 border transition-all duration-300 ${
                  isActive
                    ? 'border-[#c9a84c]/60 bg-[#c9a84c]/10'
                    : isDone
                    ? 'border-[#c9a84c]/20 bg-[#c9a84c]/5'
                    : 'border-white/5 bg-transparent opacity-30'
                }`}
              >
                <div
                  className={`flex-shrink-0 w-7 h-7 flex items-center justify-center ${
                    isActive ? 'text-[#c9a84c]' : isDone ? 'text-[#c9a84c]/60' : 'text-white/20'
                  }`}
                >
                  {isActive ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : isDone ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8l3.5 3.5L13 5" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <Icon size={14} />
                  )}
                </div>
                <span
                  className={`text-xs font-medium ${
                    isActive ? 'text-[#c9a84c]' : isDone ? 'text-[#c9a84c]/60' : 'text-white/20'
                  }`}
                >
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="bg-[#1a1a2e] border border-[#c9a84c]/20 h-2 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#c9a84c] to-[#f0d080] transition-all duration-100"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-[#a89070] text-xs font-mono">
            {Math.round(Math.min(progress, 100))}%
          </span>
          <span className="text-[#a89070] text-xs">
            Procesando…
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Admin Message Alert Modal ─────────────────────────────────────────────
function AdminMessageModal({
  caseData,
  onClose,
}: {
  caseData: Case;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-[fadeInScale_0.25s_ease-out]">
        {/* Top accent bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-[#c9a84c] via-[#f0d080] to-[#c9a84c]" />

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Bell size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-xs font-bold text-amber-600 uppercase tracking-widest">
                Notificación Importante
              </p>
              <h3 className="text-base font-bold text-[#1a1a2e] leading-tight">
                Acción requerida en su proceso
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2"
            aria-label="Cerrar notificación"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Case reference */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="font-semibold text-[#1a1a2e]">{caseData.claimantName}</span>
            <span>·</span>
            <span className="font-mono">{caseData.caseNumber}</span>
          </div>

          {/* Message box */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-[#1a1a2e] leading-relaxed font-medium">
              {caseData.adminMessage}
            </p>
          </div>

          {/* Fee + Date row */}
          <div className="grid grid-cols-2 gap-3">
            {caseData.missingDocumentsFee > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
                <DollarSign size={16} className="text-red-500 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wide">
                    Monto a pagar
                  </p>
                  <p className="text-sm font-bold text-red-700 font-tabular">
                    {caseData.currency === 'USD' ? '$' : ''}{caseData.missingDocumentsFee.toLocaleString('en-US', { minimumFractionDigits: 2 })} {caseData.currency}
                  </p>
                </div>
              </div>
            )}
            {caseData.paymentDueDate && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-2">
                <Calendar size={16} className="text-blue-500 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wide">
                    Fecha límite
                  </p>
                  <p className="text-sm font-bold text-blue-700 font-tabular">
                    {caseData.paymentDueDate}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5">
          <button
            onClick={onClose}
            className="w-full bg-[#c9a84c] hover:bg-[#b8963e] active:scale-95 text-[#1a1a2e] font-bold text-sm py-3 rounded-xl transition-all duration-150 uppercase tracking-wide"
          >
            Entendido
          </button>
          <p className="text-center text-xs text-gray-400 mt-3">
            Comuníquese con su abogado asignado para más información.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function CaseSearchSection() {
  const [docNumber, setDocNumber] = useState('');
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [foundCase, setFoundCase] = useState<Case | null>(null);
  const [inputError, setInputError] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [showAdminAlert, setShowAdminAlert] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);

  const subscribeToCase = useCallback((caseId: string) => {
    if (channelRef.current) {
      const supabase = createClient();
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const supabase = createClient();
    const channel = supabase
      .channel(`case-realtime-${caseId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'cases', filter: `id=eq.${caseId}` },
        async () => {
          const updated = await getCaseById(caseId);
          if (updated) setFoundCase(updated);
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'case_updates', filter: `case_id=eq.${caseId}` },
        async () => {
          const updated = await getCaseById(caseId);
          if (updated) setFoundCase(updated);
        }
      )
      .subscribe((status) => {
        setIsLive(status === 'SUBSCRIBED');
      });

    channelRef.current = channel;
  }, []);

  useEffect(() => {
    return () => {
      if (channelRef.current) {
        const supabase = createClient();
        supabase.removeChannel(channelRef.current);
      }
    };
  }, []);

  const handleSearch = async () => {
    const cleaned = docNumber.replace(/\s/g, '');
    if (!cleaned) {
      setInputError('Por favor ingrese su número de documento.');
      return;
    }
    if (cleaned.length < 6) {
      setInputError('El número de documento debe tener al menos 6 dígitos.');
      return;
    }
    setInputError('');
    setIsLive(false);

    // Remove previous subscription
    if (channelRef.current) {
      const supabase = createClient();
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // Show dramatic tracking screen first
    setSearchState('tracking');

    // Run the actual search in parallel with the animation
    const searchPromise = findCaseByDocument(cleaned).catch(() => null);

    // Wait at least 3.2 seconds for the animation to play
    const [result] = await Promise.all([
      searchPromise,
      new Promise((resolve) => setTimeout(resolve, 10000)),
    ]);

    if (result) {
      setFoundCase(result as Case);
      setSearchState('found');
      subscribeToCase((result as Case).id);
      // Show admin alert if there's a message
      if ((result as Case).adminMessage) {
        setShowAdminAlert(true);
      }
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      setFoundCase(null);
      setSearchState('not-found');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleReset = () => {
    setDocNumber('');
    setSearchState('idle');
    setFoundCase(null);
    setInputError('');
    setIsLive(false);
    setShowAdminAlert(false);
    if (channelRef.current) {
      const supabase = createClient();
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  };

  return (
    <>
      {/* Admin message alert modal */}
      {showAdminAlert && foundCase && foundCase.adminMessage && (
        <AdminMessageModal
          caseData={foundCase}
          onClose={() => setShowAdminAlert(false)}
        />
      )}

      {/* Tracking overlay */}
      {searchState === 'tracking' && <TrackingScreen docNumber={docNumber} />}

      {/* Search Section */}
      <section className="bg-[#f5f0e8] border-b border-[#d6c9a8] py-12 sm:py-14 px-4 sm:px-6">
        <div className="max-w-screen-2xl mx-auto">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#c9a84c]/10 text-[#8a6f2e] text-xs font-semibold tracking-widest uppercase px-4 py-2 mb-5 border border-[#c9a84c]/30">
              <FileSearch size={13} />
              Consulta de Estado de Proceso
            </div>
            <h2
              className="text-[#1a1a2e] font-bold mb-3"
              style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.9rem)' }}
            >
              Ingrese su número de documento
            </h2>
            <p className="text-[#6b5c3e] text-sm mb-8 leading-relaxed">
              Consulte el estado actual de su proceso de recuperación de capital ingresando
              su número de cédula o documento de identidad.
            </p>

            {/* Search Input */}
            <div className="bg-white border border-[#d6c9a8] p-2 flex flex-col sm:flex-row gap-2 shadow-md">
              <div className="flex-1 relative">
                <Search
                  size={18}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#c9a84c]/60"
                />
                <input
                  type="text"
                  inputMode="numeric"
                  value={docNumber}
                  onChange={(e) => {
                    setDocNumber(e.target.value);
                    if (inputError) setInputError('');
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Ingrese su número de documento"
                  className="w-full pl-10 pr-4 py-3 text-sm text-[#1a1a2e] placeholder:text-[#a89070] bg-transparent outline-none"
                  aria-label="Número de documento"
                  suppressHydrationWarning
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={searchState === 'tracking' || searchState === 'loading'}
                className="flex items-center gap-2 bg-[#c9a84c] hover:bg-[#b8963e] active:scale-95 text-[#1a1a2e] font-bold text-sm px-6 py-3 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto justify-center uppercase tracking-wide"
              >
                {searchState === 'tracking' || searchState === 'loading' ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Rastreando...
                  </>
                ) : (
                  <>
                    <Search size={16} />
                    Consultar
                  </>
                )}
              </button>
            </div>

            {inputError && (
              <p className="flex items-center gap-1.5 text-red-600 text-sm mt-2 justify-center">
                <AlertCircle size={14} />
                {inputError}
              </p>
            )}

            <p className="text-[#a89070] text-xs mt-4">
              Para realizar una búsqueda coloca tu numero de documento
            </p>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="flex-1 py-8 sm:py-10 px-4 sm:px-6 bg-[#f5f0e8]">
        <div ref={resultRef} className="max-w-screen-2xl mx-auto">
          {searchState === 'not-found' && (
            <NotFoundState onReset={handleReset} docNumber={docNumber} />
          )}

          {searchState === 'found' && foundCase && (
            <div className="fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <p className="text-sm text-[#6b5c3e]">
                  Mostrando resultado para documento:{' '}
                  <span className="font-semibold text-[#1a1a2e] font-tabular">
                    {docNumber}
                  </span>
                </p>
                <div className="flex items-center gap-3">
                  {isLive && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                      <Wifi size={11} className="animate-pulse" />
                      En vivo
                    </span>
                  )}
                  <button
                    onClick={handleReset}
                    className="text-sm text-[#8a6f2e] hover:underline font-medium"
                  >
                    Nueva consulta
                  </button>
                </div>
              </div>
              <CaseResultCard caseData={foundCase} />
            </div>
          )}

          {(searchState === 'idle' || searchState === 'tracking') && (
            <div className="text-center py-14 sm:py-16">
              <div className="w-16 h-16 bg-white border border-[#d6c9a8] flex items-center justify-center mx-auto mb-4 shadow-sm">
                <FileSearch size={28} className="text-[#c9a84c]/60" />
              </div>
              <h3 className="text-base font-semibold text-[#6b5c3e] mb-2">
                Ingrese su número de documento
              </h3>
              <p className="text-sm text-[#a89070] max-w-sm mx-auto">
                Utilice el campo de búsqueda para consultar el estado de su proceso
                de recuperación de capital.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function NotFoundState({ onReset, docNumber }: { onReset: () => void; docNumber: string }) {
  return (
    <div className="max-w-lg mx-auto text-center py-14 sm:py-16 fade-in">
      <div className="w-16 h-16 bg-[#c9a84c]/10 border border-[#c9a84c]/30 flex items-center justify-center mx-auto mb-5">
        <AlertCircle size={28} className="text-[#8a6f2e]" />
      </div>
      <h3 className="text-lg font-bold text-[#1a1a2e] mb-2">
        No se encontró ningún proceso
      </h3>
      <p className="text-sm text-[#6b5c3e] mb-2">
        No existe un proceso activo asociado al documento{' '}
        <span className="font-semibold text-[#1a1a2e] font-tabular">{docNumber}</span>.
      </p>
      <p className="text-sm text-[#6b5c3e] mb-8">
        Si cree que esto es un error, comuníquese directamente con nuestra oficina
        al correo{' '}
        <a href="mailto:info@legalstatus.co" className="text-[#8a6f2e] hover:underline">
          info@legalstatus.co
        </a>{' '}
        o llame al{' '}
        <a href="tel:+576013456789" className="text-[#8a6f2e] hover:underline">
          +57 601 345 6789
        </a>
        .
      </p>
      <button
        onClick={onReset}
        className="bg-[#c9a84c] hover:bg-[#b8963e] active:scale-95 text-[#1a1a2e] font-bold text-sm px-6 py-2.5 transition-all duration-150 uppercase tracking-wide"
      >
        Intentar con otro documento
      </button>
    </div>
  );
}